// Avatar cache-busting.
//
// The browser caches images aggressively by URL — great for the common case
// (a chat's photo stays the same across renders/sessions), but it means a
// CHANGED display photo can keep showing the OLD bytes when the resolved URL
// happens to be stable (a CDN path, a long-lived presigned URL keyed only by
// the media id's container, etc.).
//
// `withAvatarCacheKey` stamps the resolved URL with the photo's STABLE content
// identity (its `avatarMediaId`). The result is:
//   - same photo  → same `cb=<mediaId>` → identical URL → served from cache,
//   - new photo   → new `cb=<mediaId>`  → new URL → fetched fresh & re-cached.
// So caching is preserved AND a photo change always invalidates the old entry.
//
// No-op for inline `data:` URLs (already unique per image) and for empty inputs.

/** Append/replace a `cb=<mediaId>` cache key on a resolved avatar URL. */
export function withAvatarCacheKey(
  url: string | undefined,
  mediaId: string | undefined,
): string | undefined {
  if (url === undefined || url.length === 0) return url;
  // Inline data URLs carry their own bytes — caching is by content already.
  if (url.startsWith('data:')) return url;
  if (mediaId === undefined || mediaId.length === 0) return url;
  // PRESIGNED URLs (AWS SigV4 / any query-string-signed URL) must NOT be
  // mutated: the signature is computed over the ENTIRE query string, so
  // appending `cb=` makes the server recompute a different signature and reject
  // the request with 403 (SignatureDoesNotMatch) — the group avatar then fails
  // to load and renders blank. These URLs are already unique per content (the
  // media id is in the path and every presign carries a fresh signature/expiry),
  // so no cache-bust param is needed or safe. Strip any prior `cb=` we may have
  // wrongly added before and return the untouched signed URL.
  if (isSignedUrl(url)) return stripCacheKey(url);
  // Strip any prior `cb=` so repeated resolves don't stack the param.
  const cleaned = stripCacheKey(url);
  const sep = cleaned.includes('?') ? '&' : '?';
  return `${cleaned}${sep}cb=${encodeURIComponent(mediaId)}`;
}

/** True for a query-string-signed URL (AWS SigV4 presign, GCS/Azure SAS, etc.)
 *  whose signature covers the whole query string — appending params breaks it. */
function isSignedUrl(url: string): boolean {
  return (
    url.includes('X-Amz-Signature=') ||
    url.includes('X-Amz-Algorithm=') ||
    url.includes('X-Goog-Signature=') ||
    /[?&]sig=/.test(url) ||
    /[?&]Signature=/.test(url)
  );
}

function stripCacheKey(url: string): string {
  const qIndex = url.indexOf('?');
  if (qIndex === -1) return url;
  const base = url.slice(0, qIndex);
  const params = url
    .slice(qIndex + 1)
    .split('&')
    .filter((p) => p.length > 0 && !p.startsWith('cb='));
  return params.length > 0 ? `${base}?${params.join('&')}` : base;
}
